import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import type { PDFDocumentLoadingTask, RenderTask } from 'pdfjs-dist';
import {
  Button,
  Caption1,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  Title3,
  makeStyles,
} from '@fluentui/react-components';
import { getStepType, pdfFieldLabel, type ConfigOf } from '@vigeo/shared';
import { tokens } from '@vigeo/ui';
import { loadPdfJs, renderPdfPage, PDF_DOCUMENT_OPTIONS } from '../../lib/pdf.js';
import type { StepPortalProps } from '../contract.js';
import {
  cssScaleFor,
  inTabOrder,
  positionFields,
  unansweredRequired,
  withRequiredCheckboxesSeeded,
  type PositionedField,
} from './overlay.js';

const useStyles = makeStyles({
  block: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  nav: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'center', flexWrap: 'wrap' },
  navButton: { minHeight: '2.75rem' },
  // The page scrolls horizontally on its own rather than making the whole portal do it.
  /**
   * Focusable, because it scrolls. A scrollable region that cannot be reached by keyboard is an
   * axe `scrollable-region-focusable` failure and, more to the point, a page of a form that a
   * keyboard user cannot pan on a narrow phone — which is this surface's whole audience.
   */
  viewport: {
    overflowX: 'auto', overflowY: 'visible', WebkitOverflowScrolling: 'touch',
    ':focus-visible': { outline: `${tokens.strokeWidthThick} solid ${tokens.colorStrokeFocus2}`, outlineOffset: tokens.spacingHorizontalXXS },
  },
  page: { position: 'relative', marginInline: 'auto' },
  canvas: { display: 'block', width: '100%', height: 'auto' },
  overlayInput: {
    position: 'absolute',
    boxSizing: 'border-box',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorBrandStroke1}`,
    borderRadius: tokens.borderRadiusSmall,
    paddingInline: tokens.spacingHorizontalXXS,
  },
});

const kernel = getStepType('pdf-form');
type Config = ConfigOf<'pdf-form'>;

const draftSchema = z
  .object({
    values: z.record(z.string(), z.union([z.string(), z.boolean()])),
    /** Set by this component, read by the kernel's readyToSubmit. See that comment. */
    complete: z.boolean(),
  })
  .partial();

/**
 * `template` now lives on `StepPortalProps` itself, injected by the runner exactly like
 * `upload` — the component needs no network of its own, and cannot reach a template version
 * later than the one its instance pinned. It stays optional there because most step types
 * render no document at all.
 */
export type PdfFormPortalProps = StepPortalProps;

/**
 * A real state form, rendered by pdf.js with real inputs positioned over it.
 *
 * The canvas is `aria-hidden` and the inputs are ordinary labelled form controls, so the form
 * is completable by keyboard and by a screen reader without seeing the page at all. That is
 * not a nicety: the alternative — drawing the fields onto the canvas — produces a document
 * nobody using assistive technology can fill, on a surface whose entire audience is field
 * staff on phones.
 *
 * Only ONE page is rendered at a time. iOS Safari caps total canvas memory, and a
 * multi-page scanned form rendered all at once is the shape that hits it — the tab is
 * discarded and the person loses everything they had typed.
 */
export function PdfFormPortal({ config, draft, onDraftChange, disabled, template, onAvailabilityChange }: PdfFormPortalProps) {
  const styles = useStyles();
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [scale, setScale] = useState(1);
  const [rendering, setRendering] = useState(true);
  const [problem, setProblem] = useState<string | null>(null);

  const parsed = kernel.configSchema.safeParse(config);
  const settings: Config | null = parsed.success ? parsed.data : null;
  const version = template?.version ?? null;

  const current = draftSchema.safeParse(draft ?? {});
  // Memoised on the draft itself: a fresh object every render would rebuild `setValue` every
  // render, and every input under it with it — on a page that may carry fifty of them.
  const values = useMemo(
    () => (current.success ? current.data.values : undefined) ?? {},
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `draft` is the real input; `current` is derived from it each render.
    [draft],
  );

  const setValue = useCallback(
    (id: string, value: string | boolean) => {
      const next = { ...values, [id]: value };
      // Written through the runner's draft channel, never a channel of its own — autosave,
      // recovery and the submit gate all read that one place. `complete` travels with the
      // values because only this component has the template and therefore knows what the
      // form requires.
      onDraftChange({
        values: next,
        complete: version === null ? false : unansweredRequired(version, next).length === 0,
      });
    },
    [onDraftChange, values, version],
  );

  /**
   * "No" has to exist before it can be sent.
   *
   * A required checkbox nobody has touched has no key in the draft, and an absent value is
   * not an answer — so the submit button never enabled for the person who correctly left the
   * barrier-crime box unticked, and the only way out was to tick it and untick it again. See
   * `withRequiredCheckboxesSeeded` for why this seeds rather than treating absence as "no".
   *
   * Written through `onDraftChange` like any other answer, so it is autosaved, recovered and
   * submitted by exactly the same path. It costs one draft write on first open, which is the
   * price of the filed form recording `false` rather than nothing.
   *
   * Terminates on its own: the write changes `values`, the next run finds every required
   * checkbox already boolean, and `withRequiredCheckboxesSeeded` returns null.
   */
  useEffect(() => {
    // `disabled` is true only while a submit is in flight. Seeding into that would race the
    // body already on its way to the server.
    if (version === null || disabled) return;
    const seeded = withRequiredCheckboxesSeeded(version, values);
    if (seeded === null) return;
    onDraftChange({
      values: seeded,
      complete: unansweredRequired(version, seeded).length === 0,
    });
  }, [version, values, disabled, onDraftChange]);

  /** pdf.js loads lazily through `loadPdfJs` (lib/pdf.ts), shared with the HR file viewer. */
  useEffect(() => {
    if (template === undefined || version === null) return;
    let cancelled = false;
    let loadingTask: PDFDocumentLoadingTask | undefined;
    let renderTask: RenderTask | undefined;

    void (async () => {
      setRendering(true);
      setProblem(null);
      onAvailabilityChange?.(false);
      try {
        const pdfjs = await loadPdfJs();

        const bytes = await template.load();
        if (cancelled) return;
        // pdf.js transfers this buffer to its worker. Keep the caller's bytes reusable.
        loadingTask = pdfjs.getDocument({ data: bytes.slice(), ...PDF_DOCUMENT_OPTIONS });
        const doc = await loadingTask.promise;
        const page = await doc.getPage(pageIndex + 1);

        const target = canvas.current;
        if (cancelled || target === null) return;

        const drawn = renderPdfPage(page, target);
        if (drawn === null) return;
        const { viewport } = drawn;
        const ratio = window.devicePixelRatio || 1;
        renderTask = drawn.task;
        await renderTask.promise;

        // See cssScaleFor: the unrotated MediaBox against an already-rotated viewport width
        // is what put every field on a /Rotate 90 page 29% out.
        if (!cancelled) { setScale(cssScaleFor(page, viewport.width, ratio)); onAvailabilityChange?.(true); }
      } catch {
        if (!cancelled) {
          setProblem('This form could not be displayed. Please contact our office.');
          onAvailabilityChange?.(false);
        }
      } finally {
        if (!cancelled) setRendering(false);
      }
    })();

    return () => {
      cancelled = true;
      renderTask?.cancel();
      void loadingTask?.destroy().catch(() => {});
    };
  }, [template, version, pageIndex, onAvailabilityChange]);

  if (settings === null) return <Text>This step is misconfigured. HR has been notified.</Text>;

  if (template === undefined || version === null) {
    return (
      <MessageBar intent="warning">
        <MessageBarBody>This form is not available yet. Our office will be in touch.</MessageBarBody>
      </MessageBar>
    );
  }

  const positioned = inTabOrder(positionFields(version, pageIndex, scale));

  return (
    <div className={styles.block}>
      <Title3>{settings.heading}</Title3>
      {settings.instructions === '' ? null : <Text>{settings.instructions}</Text>}
      {/* One page at a time, so the person needs a way to turn them. Rendering all of them
          instead is what exhausts iOS Safari's canvas budget and discards the tab. */}
      <div className={styles.nav}>
        <Button
          className={styles.navButton}
          disabled={pageIndex === 0 || rendering}
          onClick={() => { setRendering(true); onAvailabilityChange?.(false); setPageIndex((i) => Math.max(0, i - 1)); }}
        >
          Previous page
        </Button>
        <Caption1>
          Page {pageIndex + 1} of {version.pageCount}
        </Caption1>
        <Button
          className={styles.navButton}
          disabled={pageIndex >= version.pageCount - 1 || rendering}
          onClick={() => { setRendering(true); onAvailabilityChange?.(false); setPageIndex((i) => Math.min(version.pageCount - 1, i + 1)); }}
        >
          Next page
        </Button>
      </div>
      {/* Every page's answers are held in one draft, so turning the page never loses what
          was typed on the one before — the fields simply are not on screen. */}
      {(() => {
        // Named, not counted. "3 of 12 filled in" tells somebody they are not finished; it
        // does not tell them which page to go back to.
        const missing = unansweredRequired(version, values);
        return missing.length === 0 ? (
          <Caption1>Everything this form requires has been filled in.</Caption1>
        ) : (
          <Caption1>
            Still needed: {missing.map((id) => pdfFieldLabel(version.fields.find((field) => field.id === id) ?? { id, label: null })).join(', ')}.
          </Caption1>
        );
      })()}

      {problem === null ? null : (
        <MessageBar intent="error">
          <MessageBarBody>{problem}</MessageBarBody>
        </MessageBar>
      )}
      {rendering ? <Spinner size="tiny" label="Loading the form" labelPosition="after" /> : null}

      <div className={styles.viewport} tabIndex={0} role="group" aria-label={`${settings.heading}, page ${pageIndex + 1} of ${version.pageCount}`}>
        <div className={styles.page}>
          {/* Hidden from assistive tech: it is a picture of the form, and the real controls
              are the inputs over it. Announcing it would read a page of unlabelled ink. */}
          <canvas ref={canvas} className={styles.canvas} aria-hidden="true" />

          {positioned.map((f) => (
            <OverlayField
              key={f.field.id}
              positioned={f}
              value={values[f.field.id]}
              disabled={disabled || rendering || problem !== null}
              onChange={(v) => setValue(f.field.id, v)}
              className={styles.overlayInput}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function OverlayField({
  positioned,
  value,
  disabled,
  onChange,
  className,
}: {
  readonly positioned: PositionedField;
  readonly value: string | boolean | undefined;
  readonly disabled: boolean;
  readonly onChange: (value: string | boolean) => void;
  readonly className: string;
}) {
  const { field, left, top, width, height, fontSize } = positioned;
  // A real label, even though the page already shows one in ink — the ink is not readable by
  // anything but an eye.
  const label = pdfFieldLabel(field);
  const style = { left, top, width, height, fontSize };

  if (field.kind === 'checkbox') {
    return (
      <input
        type="checkbox"
        className={className}
        style={style}
        aria-label={label}
        checked={value === true}
        disabled={disabled}
        onChange={(e) => onChange(e.currentTarget.checked)}
      />
    );
  }

  return (
    <input
      type={field.kind === 'date' ? 'date' : 'text'}
      className={className}
      style={style}
      aria-label={label}
      value={typeof value === 'string' ? value : ''}
      maxLength={field.maxLength ?? undefined}
      disabled={disabled}
      onChange={(e) => onChange(e.currentTarget.value)}
    />
  );
}

/** Unused here, but the review half needs the same schema. */
export const pdfFormDraftSchema = draftSchema;
export { Field, Input };
